import apiClient from './apiClient';

/**
 * Cache für UtilityBill-Daten (Nebenkostenabrechnungen) mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * Pattern analog zu referenceDataService.js, aber mit:
 * - Kürzerer TTL (5min statt 1h) da transaktionale Daten
 * - Expliziter Invalidierung nach Mutationen
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `utilityBills:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 Minuten (transaktionale Daten)

// In-Memory Cache als Fallback (Private Mode, localStorage nicht verfügbar)
const memoryCache = new Map();
const pendingRequests = new Map();

// Invalidation-Token für Race-Condition-Schutz bei clearCache()
let cacheInvalidationToken = 0;

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__ub_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * Liest Cache-Eintrag aus localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @returns {object|null} Cache-Eintrag oder null wenn nicht vorhanden/ungültig
 */
const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Schreibt Cache-Eintrag in localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @param {object} value - Cache-Wert { data, timestamp, etag }
 */
const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

/**
 * Löscht alle Cache-Einträge aus localStorage.
 */
const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf UtilityBill-Endpoints.
 * Unterstützt localStorage-Persistenz und ETag-basierte Revalidierung.
 * @param {string} url - API-Pfad (z.B. '/utility-bills')
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<Array|object>} Die Daten (defensive Kopie bei Arrays)
 */
const getCached = (url, params, signal, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;

  // ETag senden falls vorhanden (Revalidierung)
  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache()/invalidate aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || []);
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      if (!staleEntry) {
        pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return [];
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      pendingRequests.delete(cacheKey);
      return staleEntry.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

const utilityBillCacheService = {
  /**
   * Lädt alle Nebenkostenabrechnungen (gecacht).
   * @param {object} [params] - Filter-Parameter (propertyId, year)
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<Array>} Liste der UtilityBillDTOs
   */
  getUtilityBills: (params, signal) => getCached('/utility-bills', params, signal),

  /**
   * Lädt eine einzelne Nebenkostenabrechnung mit Items (gecacht).
   * @param {string} billId - UUID der Abrechnung
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<object>} UtilityBillDTO
   */
  getUtilityBill: (billId, signal) => getCached(`/utility-bills/${billId}`, undefined, signal),

  /**
   * Invalidiert den List-Cache (nach Create, Update, Delete, Finalize).
   */
  invalidateList: () => {
    // Alle List-Cache-Einträge löschen (verschiedene Filter-Kombinationen)
    for (const key of [...memoryCache.keys()]) {
      if (key === '/utility-bills' || key.startsWith('/utility-bills?')) {
        memoryCache.delete(key);
        pendingRequests.delete(key);
        removeFromLocalStorage(key);
      }
    }
    // Auch aus localStorage löschen (falls nicht im Memory-Cache)
    if (storageAvailable) {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const storageKey = localStorage.key(i);
          if (storageKey && storageKey.startsWith(CACHE_PREFIX)) {
            const cacheKey = storageKey.substring(CACHE_PREFIX.length);
            if (cacheKey === '/utility-bills' || cacheKey.startsWith('/utility-bills?')) {
              localStorage.removeItem(storageKey);
            }
          }
        }
      } catch {
        // Ignorieren
      }
    }
  },

  /**
   * Invalidiert den Detail-Cache für eine bestimmte Abrechnung.
   * @param {string} billId - UUID der Abrechnung
   */
  invalidateDetail: (billId) => {
    const detailKey = `/utility-bills/${billId}`;
    invalidateCacheKey(detailKey);
  },

  /**
   * Speichert die CO₂-Kostenaufteilungs-Daten (CO2KostAufG) einer Abrechnung und stößt die
   * automatische Berechnung der Vermieter-Erstattung (Auto-Kostenposition CO2_RUECKERSTATTUNG)
   * an. Invalidiert Detail- und List-Cache, da sich sowohl die Item-Liste als auch der Saldo der
   * Abrechnung ändern.
   * @param {string} billId - UUID der Abrechnung
   * @param {object} payload - { version, co2AmountKg, co2CostTotal, heatedAreaSqm, buildingType,
   *   landlordShareHalved, landlordShareWaived }
   * @returns {Promise<object>} Aktualisiertes UtilityBillDTO
   */
  updateCo2Data: (billId, payload) =>
    apiClient.put(`/utility-bills/${billId}/co2-data`, payload).then(response => {
      utilityBillCacheService.invalidateDetail(billId);
      utilityBillCacheService.invalidateList();
      return response.data;
    }),

  /**
   * Entfernt die CO₂-Kostenaufteilungs-Daten und die zugehörige Auto-Kostenposition wieder.
   * @param {string} billId - UUID der Abrechnung
   * @param {number} version - Aktuelle Version der Abrechnung (Optimistic Locking)
   * @returns {Promise<object>} Aktualisiertes UtilityBillDTO
   */
  clearCo2Data: (billId, version) =>
    apiClient.delete(`/utility-bills/${billId}/co2-data`, { params: { version } }).then(response => {
      utilityBillCacheService.invalidateDetail(billId);
      utilityBillCacheService.invalidateList();
      return response.data;
    }),

  /**
   * Invalidiert alle UtilityBill-Caches (List + Detail).
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default utilityBillCacheService;
